// HTPTV-695: pure visibility and routing logic for the failed-payment recovery
// UI. Kept side-effect free so the decision (payment-problem detection,
// voluntary-cancel suppression) and the checkout route are unit-testable, and
// so both the v1 (@/Common) and v2 (@/v2) surfaces can share one copy - v2 may
// not import from @/Common, hence this lives under @/utils.
import PageRoutes from "./constants";

/** Stripe subscription statuses that mean an involuntary payment problem. */
export const PAYMENT_PROBLEM_STATUSES = ["past_due", "unpaid", "incomplete"];

/**
 * Statuses where the subscription never started, so there is nothing to pause or
 * cancel. `incomplete_expired` is deliberately absent from
 * PAYMENT_PROBLEM_STATUSES: Stripe has expired it for good, so there is no
 * invoice left to settle and no indicator is warranted, but there is also
 * nothing to cancel.
 */
export const NOT_STARTED_STATUSES = ["incomplete", "incomplete_expired"];

export interface PaymentAlertSubscription {
  status?: string;
  cancel_at_period_end?: boolean;
  canceled_at?: number | null;
}

export interface PendingPayment {
  amountDue?: number;
  priceId?: string | number;
  paymentRowId?: string | number;
  clientSecret?: string | null;
}

/**
 * A subscriber should see the indicator only when their subscription is in an
 * involuntary payment-failure state and they have not voluntarily canceled.
 */
export function shouldShowPaymentAlert(
  subscription?: PaymentAlertSubscription | null
): boolean {
  if (!subscription) return false;
  if (!PAYMENT_PROBLEM_STATUSES.includes(subscription.status ?? "")) return false;

  // Voluntary cancels chose to leave, so never nag them.
  if (subscription.cancel_at_period_end) return false;
  if (subscription.canceled_at) return false;

  return true;
}

/**
 * The in-app checkout route for an outstanding invoice, or null when there is
 * nothing to settle.
 *
 * The :clientSecret segment matters more than it looks. The Payment page
 * branches on it: a real secret drives stripe.confirmPayment against the
 * existing PaymentIntent, settling the open invoice in place. "0" instead falls
 * through to CreateSubscription, whose backend handler calls
 * cancelLastSubscription first, cancelling the live Stripe subscription and
 * voiding its unpaid invoice before creating a new one. For a
 * past_due/unpaid/incomplete subscriber that means writing off arrears and
 * resetting the billing anniversary, which is never what "pay your open
 * invoice" should do.
 *
 * So we use the real clientSecret whenever pendingPayment has one. "0" is only
 * correct for a subscription that never started and has no PaymentIntent at
 * all, where creating a fresh subscription is genuinely the right action.
 */
export function buildPayUrl(
  pendingPayment?: PendingPayment | null
): string | null {
  if (!pendingPayment) return null;
  if (!(Number(pendingPayment.amountDue) > 0)) return null;
  if (!pendingPayment.priceId || !pendingPayment.paymentRowId) return null;

  const clientSecret = pendingPayment.clientSecret
    ? String(pendingPayment.clientSecret)
    : "0";

  return PageRoutes.paymentPage
    .replace(":clientSecret", clientSecret)
    .replace(":plan_id", String(pendingPayment.priceId))
    .replace(":paymentId", String(pendingPayment.paymentRowId));
}
